import { Box, Flex, Text, Button, Progress, Divider } from '@chakra-ui/react';
import { FaMicrophone, FaStop, FaTrash } from 'react-icons/fa';

import { ElementsRenderer } from '../../shared';
import { useAuth, useParams, useAudioRecorder, useStableMediaUrl } from '../../../hooks';
import { TEXT_TYPE } from '../../../constants';
import { getMediaBlob } from '../../../utils/mediaSessionCache';
import ReadingComprehensionElement from './ReadingComprehensionElement';

const AudioRecorderQuestion = ({
  elements,
  settings,
  updateAndSaveAnswer,
  uploadedFile,
  id,
  pauseStrictMode,
  resumeStrictMode,
  readingComprehensionQuestionRef
}) => {
  const { maxDuration = 300, maxFileSize = 10, allowRetakes = true } = settings || {};
  const { studentAssignmentId } = useParams();
  const { token } = useAuth();

  const {
    isRecording,
    isUploading,
    uploadError,
    startError,
    mediaBlob,
    duration,
    startRecording,
    stopRecording,
    retryUpload,
    handleRetake
  } = useAudioRecorder({
    id,
    maxDuration,
    maxFileSize,
    studentAssignmentId,
    token,
    updateAndSaveAnswer,
    pauseStrictMode,
    resumeStrictMode,
    uploadedFileKey: uploadedFile?.fileKey
  });

  const blob =
    mediaBlob || getMediaBlob({ fileKey: uploadedFile?.fileKey, questionId: id }) || null;
  const { mediaUrl, isLoading: isMediaLoading } = useStableMediaUrl({
    blob,
    fileKey: uploadedFile?.fileKey,
    questionId: id,
    token
  });

  const hasSavedRecording = Boolean(uploadedFile?.fileKey);
  // A recording exists in memory but has not been persisted yet — either the save is still
  // settling or it failed. Without this the panel renders empty and the answer looks lost.
  const hasUnsavedRecording = Boolean(mediaBlob) && !hasSavedRecording && !isUploading;

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <Box w="100%" mt={4}>
      <Flex
        w="100%"
        className="katex-question"
        px={{ base: 2, lg: 0 }}
        flexGrow={1}
        flexDirection="column"
      >
        {readingComprehensionQuestionRef && (
          <ReadingComprehensionElement questionRef={readingComprehensionQuestionRef} />
        )}
        <Flex flexDirection={'column'} flexGrow={1}>
          <Text color={'#686868'} fontWeight={'bold'}>
            Audio Question
          </Text>
          <Flex my={2} w="100%" flexDir={'column'}>
            <Box
              fontSize="lg"
              whiteSpace="normal"
              wordBreak="break-word"
              overflowX="hidden"
              minH="50px"
              h="auto"
              className="question-container"
            >
              <ElementsRenderer elements={elements} textType={TEXT_TYPE.TIPTAP} />
            </Box>
          </Flex>
          <Divider />
        </Flex>
      </Flex>

      <Flex
        direction="column"
        align="center"
        mt={8}
        p={6}
        bg="gray.50"
        borderRadius="md"
        border="1px"
        borderColor="gray.200"
      >
        {(!uploadedFile || !uploadedFile.fileKey) && !mediaBlob && (
          <>
            <Text mb={4} color="gray.600" fontWeight="medium">
              Record your answer (Max: {formatTime(maxDuration)})
            </Text>

            <Flex align="center" gap={4}>
              {!isRecording ? (
                <Button
                  leftIcon={<FaMicrophone />}
                  colorScheme="blue"
                  onClick={startRecording}
                  size="lg"
                  rounded="full"
                >
                  Start Recording
                </Button>
              ) : (
                <>
                  <Button
                    leftIcon={<FaStop />}
                    colorScheme="red"
                    onClick={stopRecording}
                    size="lg"
                    rounded="full"
                  >
                    Stop Recording
                  </Button>
                  <Text fontSize="lg" fontWeight="bold" color="red.500" w="80px">
                    {formatTime(duration)}
                  </Text>
                </>
              )}
            </Flex>

            {isRecording && (
              <Progress
                w="100%"
                maxW="300px"
                mt={6}
                value={(duration / maxDuration) * 100}
                size="sm"
                colorScheme="blue"
                hasStripe
                isAnimated
              />
            )}

            {startError && !isRecording && (
              <Box
                mt={5}
                w="100%"
                maxW="420px"
                px={4}
                py={3}
                bg="red.50"
                border="1px"
                borderColor="red.200"
                borderRadius="md"
              >
                <Text fontSize="sm" fontWeight="semibold" color="red.700" mb={1}>
                  Recording could not start
                </Text>
                <Text fontSize="sm" color="red.700">
                  {startError}
                </Text>
              </Box>
            )}
          </>
        )}

        {isUploading && (
          <Flex direction="column" align="center" gap={2}>
            <Text color="blue.500" fontWeight="medium">
              Uploading your recording...
            </Text>
            <Progress
              isIndeterminate
              w="100%"
              maxW="300px"
              size="sm"
              colorScheme="blue"
              borderRadius="md"
            />
          </Flex>
        )}

        {hasUnsavedRecording && (
          <Flex direction="column" align="center" gap={4} w="100%">
            <Text color={uploadError ? 'red.500' : 'gray.600'} fontWeight="bold">
              {uploadError ? 'Your recording could not be saved' : 'Finishing up...'}
            </Text>

            {uploadError && (
              <Text fontSize="sm" color="gray.600" textAlign="center" maxW="400px">
                {uploadError}
              </Text>
            )}

            {mediaUrl && (
              <Box w="100%" maxW="400px" p={4} bg="white" borderRadius="md" shadow="sm">
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Your recording:
                </Text>
                <audio controls src={mediaUrl} style={{ width: '100%' }} />
              </Box>
            )}

            {uploadError && (
              <Flex gap={3} wrap="wrap" justify="center">
                <Button colorScheme="blue" onClick={retryUpload}>
                  Try again
                </Button>
                {allowRetakes && (
                  <Button
                    leftIcon={<FaTrash />}
                    variant="outline"
                    colorScheme="red"
                    onClick={handleRetake}
                  >
                    Discard & Re-record
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {hasSavedRecording && !isUploading && (
          <Flex direction="column" align="center" gap={4} w="100%">
            <Text color="green.500" fontWeight="bold">
              Recording saved successfully!
            </Text>

            {uploadedFile.fileKey && (
              <Box w="100%" maxW="400px" p={4} bg="white" borderRadius="md" shadow="sm">
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Preview:
                </Text>
                {isMediaLoading ? (
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    Loading recording...
                  </Text>
                ) : mediaUrl ? (
                  <audio controls src={mediaUrl} style={{ width: '100%' }} />
                ) : (
                  <Text fontSize="sm" color="red.500" fontStyle="italic">
                    Failed to load recording.
                  </Text>
                )}
              </Box>
            )}

            {allowRetakes && (
              <Button
                leftIcon={<FaTrash />}
                variant="outline"
                colorScheme="red"
                onClick={handleRetake}
                mt={2}
              >
                Delete & Retake
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default AudioRecorderQuestion;
